import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import { Loading, PostCardSkeleton, StackSkeleton } from '../../components/layout/Skeletons';
import { useToast } from '../../context/ToastContext';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';
import { formatOfferWindow } from '../../constants/community';

const MAX_LIVE = 3;

const EMPTY = { title: '', startsOn: '', endsOn: '', description: '' };

/** The fields this form shows, and so the only ones an error can sit under. */
const FIELDS = ['title', 'startsOn', 'endsOn', 'description'];

/** Today, as the date input wants it. */
const today = () => new Date().toISOString().slice(0, 10);

/**
 * What a shop is offering, and the form to write the next one.
 *
 * Published the moment it is saved: the listing has already been vetted, and
 * an offer for Saturday that waits for a moderator until Monday is worth
 * nothing. Three at a time at most — a shop with nine permanent "offers" is an
 * advertising board, and residents stop looking.
 */
function MyOffers() {
  const toast = useToast();

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      setLoading(true);

      const response = await apiCall('/me/offers', { method: 'GET' });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(translateApiMessage(data.error) || 'Vos offres n’ont pas pu être chargées.');
      }

      setOffers(data.data || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing('new');
    setForm({ ...EMPTY, startsOn: today(), endsOn: today() });
    setFieldErrors({});
  };

  const openEdit = (offer) => {
    setEditing(offer.uuid);
    setForm({
      title: offer.title,
      startsOn: offer.starts_on,
      endsOn: offer.ends_on,
      description: offer.description || '',
    });
    setFieldErrors({});
  };

  const update = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
      // The end follows the start when it would otherwise be behind it, which
      // is what somebody means by "just Saturday".
      ...('startsOn' === name && (!previous.endsOn || previous.endsOn < value) ? { endsOn: value } : {}),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setError('');

    if (!form.title.trim()) {
      setFieldErrors({ title: 'Donnez un titre à votre offre' });
      return;
    }

    setSaving(true);

    try {
      const creating = 'new' === editing;
      const response = await apiCall(creating ? '/me/offers' : `/me/offers/${editing}`, {
        method: creating ? 'POST' : 'PUT',
        body: JSON.stringify({
          title: form.title.trim(),
          startsOn: form.startsOn,
          endsOn: form.endsOn,
          description: form.description.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const violations = translateViolations(data.violations);
        setFieldErrors(violations);

        if (0 === Object.keys(violations).filter((field) => FIELDS.includes(field)).length) {
          setError(translateApiMessage(data.error) || 'Votre offre n’a pas pu être enregistrée.');
        }

        return;
      }

      setOffers(data.data || []);
      setEditing(null);
      toast.success(creating ? 'Votre offre est en ligne.' : 'Votre offre est à jour.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setSaving(true);

    try {
      const response = await apiCall(`/me/offers/${deleting.uuid}`, { method: 'DELETE' });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(translateApiMessage(data.error) || 'Votre offre n’a pas pu être arrêtée.');
      }

      toast.success('Offre arrêtée.');
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // What the limit is actually counted on, server-side: anything not over and
  // not taken down. An offer announced for next week is on the page too, so it
  // takes one of the three places.
  const live = offers.filter((offer) => !offer.expired && 'PUBLISHED' === offer.status);

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%', maxWidth: 820 }}>
        <PageHeader
          title="Mes offres"
          lede="Ce que vous proposez aux habitants, pour quelques jours. Une offre est visible dès que vous l’enregistrez — annoncée si elle commence plus tard — et disparaît d’elle-même à la date de fin."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openNew}
              disabled={live.length >= MAX_LIVE}
            >
              Nouvelle offre
            </Button>
          }
        />

        {live.length >= MAX_LIVE && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Vous avez {MAX_LIVE} offres en ligne ou annoncées, le maximum. Arrêtez-en une pour en
            proposer une autre — au-delà, les habitants ne les lisent plus.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading label="Chargement de vos offres">
            <StackSkeleton count={2}>{PostCardSkeleton}</StackSkeleton>
          </Loading>
        ) : offers.length > 0 ? (
          <Stack spacing={2}>
            {offers.map((offer) => (
              <Paper key={offer.uuid} variant="outlined" sx={{ p: { xs: 2.25, md: 2.75 } }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={offer.live ? 'En ligne' : offer.expired ? 'Terminée' : 'À venir'}
                    color={offer.live ? 'success' : offer.expired ? 'default' : 'info'}
                    variant={offer.live ? 'filled' : 'outlined'}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatOfferWindow(offer.starts_on, offer.ends_on)}
                  </Typography>
                </Stack>

                <Typography variant="h4" component="h2">
                  {offer.title}
                </Typography>

                {offer.description && (
                  <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: 'pre-line' }}>
                    {offer.description}
                  </Typography>
                )}

                {/* A moderator took it down after a report: the shop is told why. */}
                {offer.hidden_reason && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    Retirée par la modération : {offer.hidden_reason}
                  </Alert>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(offer)}>
                    Modifier
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleting(offer)}
                  >
                    Arrêter
                  </Button>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h3" component="p" sx={{ mb: 1 }}>
              Aucune offre pour l’instant
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Une offre attire les habitants dans votre commerce : « samedi, 2 baguettes achetées,
              la 3e offerte ».
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              Écrire ma première offre
            </Button>
          </Paper>
        )}
      </Box>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={save} noValidate>
          <DialogTitle>{'new' === editing ? 'Nouvelle offre' : 'Modifier l’offre'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <TextField
                label="L’offre"
                name="title"
                value={form.title}
                onChange={update}
                error={Boolean(fieldErrors.title)}
                helperText={fieldErrors.title || 'Par exemple : « 2 baguettes achetées, la 3e offerte »'}
                disabled={saving}
                required
                fullWidth
                inputProps={{ maxLength: 120 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Premier jour"
                  name="startsOn"
                  type="date"
                  value={form.startsOn}
                  onChange={update}
                  error={Boolean(fieldErrors.startsOn)}
                  helperText={fieldErrors.startsOn}
                  disabled={saving}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Dernier jour"
                  name="endsOn"
                  type="date"
                  value={form.endsOn}
                  onChange={update}
                  error={Boolean(fieldErrors.endsOn)}
                  helperText={fieldErrors.endsOn || 'Même jour pour une offre d’une journée'}
                  disabled={saving}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                label="Précisions"
                name="description"
                value={form.description}
                onChange={update}
                error={Boolean(fieldErrors.description)}
                helperText={fieldErrors.description || 'Les conditions, s’il y en a : quantités, produits concernés…'}
                disabled={saving}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditing(null)} disabled={saving} variant="outlined">
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Mettre en ligne'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Arrêter cette offre ?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            « {deleting?.title} » disparaîtra des offres du quartier. Vous pourrez toujours en
            écrire une nouvelle.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={saving} variant="outlined">
            Annuler
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={saving}>
            Arrêter
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default MyOffers;
